import { useI18n, type MessageKey } from '@ecom/i18n';
import { Badge, type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import type { DerivedStock, StockState } from '../types';

const TONE_BY_STATE: Record<StockState, BadgeTone> = {
  in_stock: 'good',
  low_stock: 'warn',
  out_of_stock: 'critical',
  temporarily_unavailable: 'neutral',
};

const LABEL_KEY_BY_STATE: Record<StockState, MessageKey> = {
  in_stock: 'merchant.products.stock.inStock',
  low_stock: 'merchant.products.stock.lowStock',
  out_of_stock: 'merchant.products.stock.outOfStock',
  temporarily_unavailable: 'merchant.products.stock.unavailable',
};

/**
 * `quantityAvailable` shown alongside the state — both come from
 * `DerivedStock` (inventory-state-resolver.ts), computed server-side at
 * read time, never re-derived here.
 */
export function StockStateBadge({ stock }: { stock: DerivedStock }): ReactElement {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2">
      <Badge tone={TONE_BY_STATE[stock.state]}>{t(LABEL_KEY_BY_STATE[stock.state])}</Badge>
      {stock.quantityAvailable !== null ? (
        <span className="text-sm text-inkFaint">
          {t('merchant.products.stock.units', {
            count: stock.quantityAvailable,
            plural: stock.quantityAvailable > 1 ? 's' : '',
          })}
        </span>
      ) : null}
    </div>
  );
}
